import { z } from "zod";
import type {
  $brand,
  $ZodBranded,
  $ZodType,
  $ZodTypes,
  JSONSchemaMeta,
} from "zod/v4/core";

export const AutoPopulateBrand = "autoPopulate" as const;

export interface AutoPopulate {
  when: "insert" | "update" | "always";
  value: "id" | "timestamp" | "userId";
}

declare module "zod/v4/core" {
  interface GlobalMeta extends JSONSchemaMeta {
    autoPopulate?: AutoPopulate;
    // The BSON type a custom schema (z.instanceof) stands for, which is how
    // validateSchema knows it means something.
    bsonType?: string;
    uniqueItems?: boolean;
  }
}

type AutoPopulatedResult<
  T extends z.ZodType,
  A extends AutoPopulate,
> = $ZodBranded<
  A["when"] extends "update" ? z.ZodOptional<T> : T,
  typeof AutoPopulateBrand,
  "in"
>;

export const autoPopulated = <T extends z.ZodType, A extends AutoPopulate>(
  schema: T,
  auto: A,
): AutoPopulatedResult<T, A> => {
  const inner = auto.when === "update" ? schema.optional() : schema;
  return inner
    .meta({ autoPopulate: auto })
    .brand<typeof AutoPopulateBrand, "in">(AutoPopulateBrand) as any;
};

export type IsAutoPopulated<T> =
  T extends $brand<typeof AutoPopulateBrand> ? true : false;

export interface SchemaMetadata {
  autoPopulated: Record<string, AutoPopulate>;
  defaults: Record<string, () => unknown>;
}

export const collectSchemaMetadata = (schema: $ZodType): SchemaMetadata => {
  const result: SchemaMetadata = {
    autoPopulated: {},
    defaults: {},
  };

  const inner = (raw: $ZodType, key: string | undefined, depth: number) => {
    const schema = raw as unknown as $ZodTypes;
    const { def } = schema._zod;

    const meta = z.globalRegistry.get(schema);
    if (meta?.autoPopulate) {
      if (key === undefined || depth > 1) {
        throw new Error(
          `Auto-populated fields must be at the top level of the schema`,
        );
      }

      result.autoPopulated[key] = meta.autoPopulate;
      return;
    }

    switch (def.type) {
      case "object":
        for (const [shapeKey, field] of Object.entries(def.shape)) {
          if (field === undefined) continue;
          inner(field as z.ZodType, shapeKey, depth + 1);
        }
        break;

      case "union":
        for (const option of def.options) {
          inner(option, key, depth + 1);
        }
        break;

      case "array":
        inner(def.element, key, depth + 1);
        break;

      case "tuple":
        for (const item of def.items) {
          inner(item, key, depth + 1);
        }
        break;

      case "record":
      case "map":
      case "set":
        inner(def.valueType, key, depth + 1);
        break;

      case "lazy":
        inner(def.getter(), key, depth + 1);
        break;

      case "intersection":
        inner(def.left, key, depth);
        inner(def.right, key, depth);
        break;

      case "default":
      case "prefault":
        if (depth > 1) {
          throw new Error(
            `Default values must be at the top level of the schema`,
          );
        }
        if (key !== undefined) {
          // Accessing def.defaultValue invokes the default factory, so we
          // defer the access until each write (rather than capturing a single
          // value here) so that mutable defaults like [] aren't shared
          // between documents.
          result.defaults[key] = () => def.defaultValue;
        }
        inner(def.innerType, key, depth);
        break;

      case "nullable":
      case "optional":
      case "nonoptional":
      case "readonly":
        inner(def.innerType, key, depth);
        break;

      default:
        // No-op
        break;
    }
  };

  inner(schema, undefined, 0);
  return result;
};
